import { useState, useEffect, useCallback, useRef } from "react"
import { useNavigate } from "react-router"
import { format, subMonths, addMonths, parseISO } from "date-fns"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import Modal from "../Modal"
import { api } from "../../lib/api"
import type { ApiError } from "../../lib/api"
import { writeAiReportNav } from "../../lib/aiReportNav"
import { getMockAiReportContent } from "../../lib/aiReportMock"
import { formatMonthLabel, calcMinMonth } from "../../lib/month"

// "YYYY-MM-01" 形式の月加減算ヘルパー（date-fns 直呼び出し・toISOString は使わない）
const shiftMonth = (isoMonth: string, diff: number): string =>
  format(addMonths(parseISO(isoMonth), diff), "yyyy-MM-01")
import type { AiReportListItem, AiReportDetail, AiReportSection, AiReportContent, MonthlyReportData } from "../../types/analytics"

const POLLING_INTERVAL_MS = 5000
const MAX_POLL_COUNT      = 36

const monthStorageKey = (accountId: number) => `lyto.dashboardReportMonth:${accountId}`

const readStoredMonth = (accountId: number, fallback: string): string => {
  try {
    const v = sessionStorage.getItem(monthStorageKey(accountId))
    return v ?? fallback
  } catch {
    return fallback
  }
}

const saveStoredMonth = (accountId: number, month: string): void => {
  try { sessionStorage.setItem(monthStorageKey(accountId), month) } catch {}
}

const LABELS: Record<"competitor" | "funnel" | "fan_user", string> = {
  competitor: "競合分析",
  funnel:     "ファネル分析",
  fan_user:   "ファンユーザー分析",
}

interface SourceAvailability {
  competitor: boolean
  funnel:     boolean
  fan_user:   boolean
}

interface Props {
  accountId:          number
  accountCreatedAt:   string
  canEdit:            boolean
  // AI機能がプランに含まれない場合 true。API を呼ばずサンプルを表示する
  locked:             boolean
  // pptx出力機能がプランに含まれる場合 true（サーバー側でも Pundit で認可される）
  pptxEnabled:        boolean
  onViewSourcePeriod: (month: string) => void
}

const DashboardReportPanel = ({ accountId, accountCreatedAt, canEdit, locked, pptxEnabled, onViewSourcePeriod }: Props) => {
  const navigate = useNavigate()

  const prevMonth   = format(subMonths(new Date(), 1), "yyyy-MM-01")
  const minMonth    = calcMinMonth(accountCreatedAt)
  const tooNew      = minMonth > prevMonth

  const [selectedMonth,  setSelectedMonth]  = useState<string>(() => {
    const stored = readStoredMonth(accountId, prevMonth)
    // 範囲外なら先月にフォールバック
    if (stored < minMonth || stored > prevMonth) return prevMonth
    return stored
  })
  const [reportsByMonth, setReportsByMonth] = useState<Record<string, AiReportListItem>>({})
  const [detail,         setDetail]         = useState<AiReportDetail | null>(null)
  const [sources,        setSources]        = useState<SourceAvailability | null>(null)
  const [loading,        setLoading]        = useState(true)
  const [actionLoading,  setActionLoading]  = useState(false)
  const [showModal,      setShowModal]      = useState(false)
  const [errorMessage,   setErrorMessage]   = useState<string | null>(null)
  const [exporting,      setExporting]      = useState(false)
  const [showPptxModal,  setShowPptxModal]  = useState(false)

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollCountRef = useRef(0)

  const canGoPrev = selectedMonth > minMonth
  const canGoNext = selectedMonth < prevMonth

  // 保存済みの表示月を範囲内に補正して解決する（初期化・アカウント切替で共用）
  const resolveStoredMonth = (id: number): string => {
    const stored = readStoredMonth(id, prevMonth)
    return (stored >= minMonth && stored <= prevMonth) ? stored : prevMonth
  }

  const listItem = reportsByMonth[selectedMonth] ?? null

  // ─── クリーンアップ ────────────────────────────────────────

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null }
    pollCountRef.current = 0
  }, [])

  useEffect(() => () => clearPoll(), [clearPoll])

  // アカウント切替時にリセット（新アカウントの保存月を読み込む）
  useEffect(() => {
    setSelectedMonth(resolveStoredMonth(accountId))
    setReportsByMonth({})
    setDetail(null)
    setSources(null)
    setErrorMessage(null)
    setShowPptxModal(false)
    clearPoll()
  }, [accountId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── データ取得 ──────────────────────────────────────────────

  const fetchReports = useCallback(async () => {
    const params = new URLSearchParams({
      instagram_account_id: String(accountId),
      report_type:          "dashboard",
    })
    const res = await api.get<AiReportListItem[]>(`/analytics/ai_reports?${params}`)
    const byMonth: Record<string, AiReportListItem> = {}
    res.forEach(r => { byMonth[r.target_month] = r })
    setReportsByMonth(byMonth)
    return byMonth
  }, [accountId])

  const fetchDetail = useCallback(async (reportId: number) => {
    const params = new URLSearchParams({ instagram_account_id: String(accountId) })
    const res = await api.get<AiReportDetail>(`/analytics/ai_reports/${reportId}?${params}`)
    setDetail(res)
  }, [accountId])

  const fetchSources = useCallback(async (month: string) => {
    const params = new URLSearchParams({
      instagram_account_id: String(accountId),
      target_month:         month,
    })
    const res = await api.get<SourceAvailability>(`/analytics/ai_reports/sources?${params}`)
    setSources(res)
    return res
  }, [accountId])

  // ─── 初期化 ──────────────────────────────────────────────────

  useEffect(() => {
    if (locked) { setLoading(false); return }
    if (tooNew) { setLoading(false); return }
    const init = async () => {
      setLoading(true)
      try {
        // selectedMonth state はアカウント切替リセットの反映前の可能性があるため、
        // 表示月をここでも解決する（表示月と取得した詳細の月がズレると、混在月の
        // pptx が出力されてしまう）
        const month   = resolveStoredMonth(accountId)
        const byMonth = await fetchReports()
        const item    = byMonth[month]
        if (item?.status === "completed") await fetchDetail(item.id)
        else if (item?.status === "generating") startPolling(item.id)
        await fetchSources(month)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [accountId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── ポーリング ─────────────────────────────────────────────

  const startPolling = useCallback((reportId: number) => {
    clearPoll()
    const poll = async () => {
      if (pollCountRef.current >= MAX_POLL_COUNT) { clearPoll(); return }
      pollCountRef.current += 1

      const byMonth = await fetchReports()
      const updated = byMonth[selectedMonth] ?? Object.values(byMonth).find(r => r.id === reportId)

      if (!updated || updated.status === "generating" || updated.status === "pending") {
        pollTimerRef.current = setTimeout(poll, POLLING_INTERVAL_MS)
      } else {
        clearPoll()
        if (updated.status === "completed") await fetchDetail(reportId)
      }
    }
    pollTimerRef.current = setTimeout(poll, POLLING_INTERVAL_MS)
  }, [clearPoll, fetchReports, fetchDetail, selectedMonth])

  // ─── 月変更 ─────────────────────────────────────────────────

  const handleMonthChange = async (month: string) => {
    setSelectedMonth(month)
    saveStoredMonth(accountId, month)
    setDetail(null)
    setSources(null)
    clearPoll()

    const item = reportsByMonth[month]
    if (item?.status === "completed") await fetchDetail(item.id)
    else if (item?.status === "generating") startPolling(item.id)

    await fetchSources(month)
  }

  // ─── 生成 ────────────────────────────────────────────────────

  const doGenerate = async () => {
    setActionLoading(true)
    setErrorMessage(null)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(accountId) })
      const res = await api.post<AiReportListItem>(`/analytics/ai_reports?${params}`, {
        report_type:  "dashboard",
        target_month: selectedMonth,
      })
      setReportsByMonth(prev => ({ ...prev, [selectedMonth]: { ...res, status: "generating" } }))
      startPolling(res.id)
    } catch (err) {
      setErrorMessage((err as ApiError).data?.error ?? "レポートの生成開始に失敗しました")
    } finally {
      setActionLoading(false)
    }
  }

  const handleGenerateClick = async () => {
    const allReady = sources && sources.competitor && sources.funnel && sources.fan_user
    if (!allReady) { setShowModal(true); return }
    await doGenerate()
  }

  // ─── pptx出力 ────────────────────────────────────────────────

  const canExport = listItem?.status === "completed" && detail?.status === "completed"

  // 生成済みダッシュボードレポートに含まれなかった参照元レポート
  const missingReferencedTypes = (["competitor", "funnel", "fan_user"] as const).filter(
    t => detail?.referenced_report_ids?.[t] == null
  )

  // レポート本文（sections JSON）のパース。表示と pptx 出力で共用する
  const parseSections = (content: string | null): AiReportSection[] => {
    try {
      return (JSON.parse(content ?? "") as AiReportContent).sections ?? []
    } catch {
      return []
    }
  }

  // ダッシュボードレポートが参照した個別レポート（競合・ファネル・ファンユーザー）の詳細を取得する。
  // 取得に失敗したレポートは pptx から除外して継続する（出力全体は失敗させない）
  const fetchReferencedReport = (reportId: number | undefined): Promise<AiReportDetail | null> => {
    if (reportId == null) return Promise.resolve(null)
    const params = new URLSearchParams({ instagram_account_id: String(accountId) })
    return api.get<AiReportDetail>(`/analytics/ai_reports/${reportId}?${params}`).catch(() => null)
  }

  const doExport = async () => {
    if (!detail) return
    setExporting(true)
    setErrorMessage(null)
    try {
      const params = new URLSearchParams({
        instagram_account_id: String(accountId),
        month:                selectedMonth,
      })
      const refs = detail.referenced_report_ids ?? {}
      const [data, competitor, funnel, fanUser] = await Promise.all([
        api.get<MonthlyReportData>(`/analytics/monthly_report?${params}`),
        fetchReferencedReport(refs.competitor),
        fetchReferencedReport(refs.funnel),
        fetchReferencedReport(refs.fan_user),
      ])

      const sections = parseSections(detail.content)

      // pptxgenjs ごと遅延ロードする（初期バンドルに含めない）
      const { generateMonthlyReportPptx } = await import("../../lib/pptx/generateMonthlyReportPptx")
      await generateMonthlyReportPptx({
        data,
        aiReports: { dashboardSections: sections, competitor, funnel, fanUser },
      })
    } catch (err) {
      setErrorMessage((err as ApiError).data?.error ?? "pptxの出力に失敗しました")
    } finally {
      setExporting(false)
    }
  }

  const handleExportClick = async () => {
    if (missingReferencedTypes.length > 0) { setShowPptxModal(true); return }
    await doExport()
  }

  // ─── レンダリング ────────────────────────────────────────────

  const renderMonthSelector = () => {
    const prevM = canGoPrev ? shiftMonth(selectedMonth, -1) : null
    const nextM = canGoNext ? shiftMonth(selectedMonth, 1)  : null

    return (
      <div className="ai-report-month-selector">
        <button
          className="ai-report-month-selector__arrow"
          disabled={!canGoPrev}
          onClick={() => prevM && handleMonthChange(prevM)}
        >
          <i className="bi bi-chevron-left" />
        </button>

        {prevM ? (
          <button className="ai-report-month-selector__side" onClick={() => handleMonthChange(prevM)}>
            {formatMonthLabel(prevM)}
          </button>
        ) : (
          <div className="ai-report-month-selector__side ai-report-month-selector__side--disabled" />
        )}

        <div className="ai-report-month-selector__month">{formatMonthLabel(selectedMonth)}</div>

        {nextM ? (
          <button className="ai-report-month-selector__side" onClick={() => handleMonthChange(nextM)}>
            {formatMonthLabel(nextM)}
          </button>
        ) : (
          <div className="ai-report-month-selector__side ai-report-month-selector__side--disabled" />
        )}

        <button
          className="ai-report-month-selector__arrow"
          disabled={!canGoNext}
          onClick={() => nextM && handleMonthChange(nextM)}
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    )
  }

  const renderGenerateArea = () => (
    <div className="ai-report__generate">
      <p className="ai-report__desc">
        参照元：ダッシュボード上の情報＋生成済みの各AIレポート<br />
        登録から2ヶ月以上経過したアカウントで利用できます<br />
        一度生成すると作り直せません。各レポートを揃えてから生成することをおすすめします
      </p>
      {!listItem?.has_enough_data && (
        <p className="ai-report__no-data">対象月のデータが不足しているため、生成できません。</p>
      )}
      {canEdit && (
        <button
          className="ai-report__btn"
          onClick={handleGenerateClick}
          disabled={actionLoading || listItem?.has_enough_data === false}
        >
          {actionLoading ? "処理中…" : "生成する"}
        </button>
      )}
    </div>
  )

  const renderSourceLinks = () => {
    if (!detail || detail.status !== "completed") return null
    const types = (["competitor", "funnel", "fan_user"] as const).filter(
      t => detail.referenced_report_ids?.[t] != null
    )
    if (types.length === 0) return null

    return (
      <div className="dashboard-report__source-links">
        {types.map(t => (
          <button
            key={t}
            className="ai-report-fan-ranking__link"
            onClick={() => {
              writeAiReportNav(accountId, {
                activeType: t,
                selectedMonth,
                selectedCompetitorId: t === "competitor"
                  ? (detail.referenced_competitor_account_id ?? null)
                  : undefined,
              })
              navigate("/ai-report")
            }}
          >
            {LABELS[t]}を詳しく見る <i className="bi bi-arrow-right" />
          </button>
        ))}
      </div>
    )
  }

  const renderContent = () => {
    if (!detail?.content) return null
    const sections = parseSections(detail.content)
    if (sections.length === 0) return <p className="ai-report__muted">レポートの表示に失敗しました</p>

    return (
      <div className="ai-report__content">
        {sections.map((section, i) => (
          <div key={i} className="ai-report__section">
            <h3 className="ai-report__section-title">{section.title}</h3>
            <div className="ai-report__section-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderMockContent = () => {
    const { sections } = getMockAiReportContent("dashboard")
    return (
      <div className="ai-report__content">
        {sections.map((section, i) => (
          <div key={i} className="ai-report__section">
            <h3 className="ai-report__section-title">{section.title}</h3>
            <div className="ai-report__section-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // サンプル表示（バッジ＋案内帯＋静的モック）。案内文言のみ差し替える。
  // プランロック時・登録直後（tooNew）で共通利用する
  const renderSample = (notice: string) => (
    <div className="dashboard-report">
      <div className="dashboard-report__header">
        <div className="ai-report__sample-head">
          <span className="ai-report__sample-badge">
            <i className="bi bi-stars" /> サンプル
          </span>
        </div>
        <div className="ai-report__sample-notice">
          <i className="bi bi-info-circle" />
          <span>{notice}</span>
        </div>
      </div>
      <div className="dashboard-report__body">
        {renderMockContent()}
      </div>
    </div>
  )

  const renderMissingReportNames = () => {
    if (!sources) return ""
    return (["competitor", "funnel", "fan_user"] as const)
      .filter(t => !sources[t])
      .map(t => LABELS[t])
      .join("・")
  }

  const renderPptxButton = () => (
    <button
      className="dashboard-report__pptx-btn"
      onClick={handleExportClick}
      disabled={!canExport || exporting}
      title={canExport ? undefined : "ダッシュボードレポートの生成後に出力できます"}
    >
      {exporting ? (
        <>
          <i className="bi bi-arrow-repeat ai-report__icon-spin" /> 出力中…
        </>
      ) : (
        <>
          <i className="bi bi-file-earmark-slides" /> 月次レポート（pptx）を出力
        </>
      )}
    </button>
  )

  // ─── メインレンダリング ──────────────────────────────────────

  // プランロック時: API を呼ばずサンプルを表示（locked が tooNew より優先）
  if (locked) return renderSample("AIレポートはプロプラン以上でご利用いただけます。以下はサンプルです。")

  if (loading) return (
    <div className="dashboard-report">
      <div className="dashboard-report__header">
        <p className="ai-report__muted">読み込み中…</p>
      </div>
    </div>
  )

  // 登録から2ヶ月未満: API を呼ばずサンプルを表示
  if (tooNew) return renderSample("登録から2ヶ月以上経過すると生成できます。それまでサンプルをご覧ください。")

  const status = listItem?.status ?? null

  return (
    <div className="dashboard-report">
      <div className="dashboard-report__header">
        {renderMonthSelector()}

        {errorMessage && <p className="ai-report__error">{errorMessage}</p>}

        {status === null && renderGenerateArea()}
        {status === "pending" && renderGenerateArea()}

        {status === "generating" && (
          <p className="ai-report__generating-label">
            <i className="bi bi-arrow-repeat ai-report__icon-spin" />
            生成中です…しばらくお待ちください
          </p>
        )}

        {status === "failed" && (
          <p className="ai-report__error">生成に失敗しました。</p>
        )}

        {status === "completed" && detail && (
          <>
            <div className="dashboard-report__actions">
              <button
                className="dashboard-report__source-btn"
                onClick={() => onViewSourcePeriod(selectedMonth)}
              >
                <i className="bi bi-bar-chart" /> 生成当初のアナリティクスを見る
              </button>
              {pptxEnabled && renderPptxButton()}
            </div>
            {renderSourceLinks()}
          </>
        )}
        {/* completed かつ detail 取得済みのときは上の actions 行に表示する。
            それ以外（未生成・生成中・detail 取得前/失敗）は disabled ボタンを単独表示 */}
        {(status !== "completed" || !detail) && pptxEnabled && renderPptxButton()}
      </div>

      <div className="dashboard-report__body">
        {status === "completed" && detail
          ? renderContent()
          : status === null || status === "pending"
          ? null
          : null
        }
      </div>

      {showModal && (
        <Modal
          title="一部のレポートが未生成です"
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button
                className="ai-report__btn"
                onClick={() => { setShowModal(false); doGenerate() }}
              >
                このまま生成
              </button>
              <button
                className="ai-report__btn ai-report__btn--secondary"
                onClick={() => {
                  setShowModal(false)
                  navigate("/ai-report")
                }}
              >
                各レポートを作る
              </button>
            </>
          }
        >
          <p>
            {renderMissingReportNames()}がまだ生成されていません。<br />
            これらを除いて作成します。よろしいですか？
          </p>
        </Modal>
      )}

      {showPptxModal && (
        <Modal
          title="一部のレポートが含まれていません"
          onClose={() => setShowPptxModal(false)}
          footer={
            <>
              <button
                className="ai-report__btn"
                onClick={() => { setShowPptxModal(false); doExport() }}
              >
                このまま出力
              </button>
              <button
                className="ai-report__btn ai-report__btn--secondary"
                onClick={() => setShowPptxModal(false)}
              >
                キャンセル
              </button>
            </>
          }
        >
          <p>
            {missingReferencedTypes.map(t => LABELS[t]).join("・")}は生成時に参照されていません。<br />
            AIレポートの章はこれらを除いた内容で出力されます。よろしいですか？
          </p>
        </Modal>
      )}
    </div>
  )
}

export default DashboardReportPanel
